/**
 * Permit Visibility — Phase 1C derived selectors.
 *
 * READ-ONLY pure helpers over existing canonical sources:
 *   - permitDetail.lastUpdate
 *   - municipalityLog (via municipalityLog.ts)
 *   - requirementChecklist (via requirementsChecklist.ts)
 *   - requirements profile (via requirements.ts)
 *
 * No writers. No persistence. No telemetry sinks. No automation.
 *
 * Surfaces:
 *   - getStageAge(): days since lastUpdate + tone bucket
 *   - getOverdueFollowUps(): live (non-superseded) interactions whose
 *     followUpDate is in the past
 *   - isUsingGenericProfile(): municipality has no seeded profile
 *   - getPermitRowSummary(): everything a list row needs in one shot
 */
import type { Job } from "@/data/types";
import {
  getMunicipalityLog,
  isSupersededInteraction,
  type MunicipalityInteraction,
} from "./municipalityLog";
import { resolveJobRequirements } from "./requirementsChecklist";
import { getRequirementProfile } from "./requirements";

export type StageAgeTone = "neutral" | "amber" | "red";

export interface StageAge {
  /** Whole days since lastUpdate. Null when lastUpdate is missing/invalid. */
  days: number | null;
  tone: StageAgeTone;
  /** "—", "2d in stage", etc. */
  label: string;
}

const MS_PER_DAY = 86_400_000;

/**
 * Pure: derive stage age from permitDetail.lastUpdate.
 * Threshold buckets (locked to spec):
 *   < 3d → neutral
 *   3–7d → amber
 *   > 7d → red
 */
export function getStageAge(job: Job | null | undefined, now: Date = new Date()): StageAge {
  const last = job?.permitDetail?.lastUpdate;
  if (!last) return { days: null, tone: "neutral", label: "—" };
  const t = Date.parse(last);
  if (Number.isNaN(t)) return { days: null, tone: "neutral", label: "—" };
  const days = Math.max(0, Math.floor((now.getTime() - t) / MS_PER_DAY));
  const tone: StageAgeTone = days > 7 ? "red" : days >= 3 ? "amber" : "neutral";
  return { days, tone, label: `${days}d in stage` };
}

/**
 * All live (non-superseded) interactions whose followUpDate is in the past.
 */
export function getOverdueFollowUps(
  job: Job | null | undefined,
  now: Date = new Date(),
): MunicipalityInteraction[] {
  const log = getMunicipalityLog(job);
  if (log.length === 0) return [];
  return log.filter((entry) => {
    if (isSupersededInteraction(entry, log)) return false;
    if (!entry.followUpDate) return false;
    const t = Date.parse(entry.followUpDate);
    if (Number.isNaN(t)) return false;
    return t < now.getTime();
  });
}

/** True when the resolved profile is the generic fallback (no jurisdiction match). */
export function isUsingGenericProfile(job: Job | null | undefined): boolean {
  const profile = getRequirementProfile(job?.permitDetail?.municipality);
  return profile.municipalityId === "generic";
}

export interface PermitRowSummary {
  readinessPct: number | null;
  /** Pending requirement count (excludes complete + n_a). */
  openRequirements: number;
  interactionCount: number;
  overdueFollowUpCount: number;
  stageAge: StageAge;
  usesGenericProfile: boolean;
  profileLabel: string;
}

/**
 * Single-shot summary for list rows + the detail drawer strip.
 * Pure derivation — never mutates the job.
 */
export function getPermitRowSummary(
  job: Job | null | undefined,
  now: Date = new Date(),
): PermitRowSummary {
  const { readiness } = resolveJobRequirements(job);
  const log = getMunicipalityLog(job);
  const overdue = getOverdueFollowUps(job, now);
  const profile = getRequirementProfile(job?.permitDetail?.municipality);
  return {
    readinessPct: readiness.readinessPct,
    openRequirements: readiness.incompleteCount,
    interactionCount: log.length,
    overdueFollowUpCount: overdue.length,
    stageAge: getStageAge(job, now),
    usesGenericProfile: profile.municipalityId === "generic",
    profileLabel: profile.label,
  };
}
